import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  /** Image source; when absent the fallback content renders. */
  src?: string;
  /** Image alt text; also the source of derived initials when no src/children. */
  alt?: string;
  /** sm=32px, md=40px (default), lg=56px, or an explicit pixel size. */
  size?: "sm" | "md" | "lg" | number;
  /** Fallback content; renders in place of the image when no src is set. */
  children?: ReactNode;
}

const sizeClass = {
  sm: "size-8",
  md: "size-10",
  lg: "size-14",
} as const;

/** First letters of the first two words, uppercased ("Billy M" → "BM"). */
function initialsFrom(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .filter((word) => {
      return word.length > 0;
    })
    .slice(0, 2)
    .map((word) => {
      return word.charAt(0).toUpperCase();
    })
    .join("");
  return letters;
}

/** Circular identity chip — blue-grey disc with white label, image fills when set. */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, size = "md", className, style, children, ...props }, ref) => {
    const numericSize = typeof size === "number" ? size : undefined;
    const sizingStyle: CSSProperties | undefined = numericSize
      ? { width: `${numericSize}px`, height: `${numericSize}px` }
      : undefined;
    const sizingClass = typeof size === "number" ? undefined : sizeClass[size];

    let content: ReactNode = null;
    if (src) {
      content = <img src={src} alt={alt ?? ""} className="h-full w-full object-cover" />;
    } else if (children) {
      content = children;
    } else if (alt) {
      content = initialsFrom(alt);
    }

    return (
      <div
        ref={ref}
        className={cn(
          "type-label flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-inverse text-on-inverse",
          sizingClass,
          className,
        )}
        style={{ ...sizingStyle, ...style }}
        {...props}
      >
        {content}
      </div>
    );
  },
);
Avatar.displayName = "Avatar";
